import { useEffect, useLayoutEffect, useRef } from "react";

import {
	CompactSelect,
	type CompactSelectOption,
} from "newui/components/ui/InputWithLabel";
import { getCompactInputClasses } from "newui/components/ui/InputWithLabel/inputStyles";
import { cn } from "utils/helpers";

import type {
	ColumnConfig,
	QuoteColumnType,
} from "../../SpreadsheetEditor";
import { SAMPLE_CELL_LABELS } from "../../SpreadsheetEditor";
import type { ScopeItemRowDraft } from "../ScopeItemRowsEditor";

const CELL_BASE = "border border-[#EAECF0] px-1 py-1 text-sm";

/**
 * Auto-growing single-line-ish textarea used in template cells. CompactTextInput
 * is a plain `<input>` which truncates long content; a textarea wraps and grows
 * to fit so procurers can see what they pasted/typed.
 *
 * Uses the same compact input classes as CompactTextInput for visual parity.
 */
interface AutoGrowCellInputProps
	extends Omit<
		React.TextareaHTMLAttributes<HTMLTextAreaElement>,
		"value" | "onChange"
	> {
	value: string;
	onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

const AutoGrowCellInput: React.FC<AutoGrowCellInputProps> = ({
	value,
	onChange,
	className = "",
	...rest
}) => {
	const ref = useRef<HTMLTextAreaElement>(null);

	const resize = () => {
		const el = ref.current;
		if (!el) return;
		el.style.height = "auto";
		el.style.height = `${el.scrollHeight}px`;
	};

	// useLayoutEffect prevents a flash of the wrong height on mount; the value
	// effect handles programmatic updates (e.g. paste populating many rows).
	useLayoutEffect(resize, []);
	useEffect(resize, [value]);

	return (
		<textarea
			ref={ref}
			value={value}
			onChange={(e) => {
				onChange(e);
				resize();
			}}
			rows={1}
			className={getCompactInputClasses(
				false,
				`text-sm resize-none overflow-hidden leading-snug ${className}`
			)}
			{...rest}
		/>
	);
};

export interface TemplateRowCellProps {
	column: ColumnConfig;
	row: ScopeItemRowDraft;
	rowIndex: number;
	/** localId of the column bound to row.unitId (first unit_of_measurement). */
	unitColumnId: string | null;
	unitOptions: CompactSelectOption[];
	/** Force red-border styling on the unit cell (e.g. mandatory-UoM violation). */
	unitHasError?: boolean;
	onChangeRow: (
		rowId: number,
		field: "unitId",
		value: number | null
	) => void;
	/**
	 * Procurer-filled value for a Description column, keyed by the column's
	 * stable `localId`. All description columns route through this — there is
	 * no separate "first description -> row.name" slot anymore.
	 */
	onChangeCell?: (
		rowId: number,
		columnLocalId: string,
		value: string
	) => void;
	/**
	 * Optional handler for clipboard pastes that look like a tab/newline-separated
	 * matrix (Excel / Google Sheets). When supplied and the pasted content is
	 * multi-cell, the parent expands it across columns and down rows.
	 */
	onPasteMatrix?: (rowId: number, columnLocalId: string, text: string) => void;
}

const cellHint = (type: QuoteColumnType): string => {
	if (type === "total") return "Auto-calculated from Response: Value columns";
	return "Supplier will fill this in";
};

/**
 * Renders a single `<td>` in a template row, dispatched on the column type:
 *  - Description column       -> AutoGrowCellInput bound to row.cells[localId]
 *                                (uniform across all description columns —
 *                                cells-only, no row.name slot)
 *  - first Units column       -> CompactSelect bound to row.unitId
 *  - everything else          -> greyed sample-text placeholder
 */
export const TemplateRowCell: React.FC<TemplateRowCellProps> = ({
	column,
	row,
	rowIndex,
	unitColumnId,
	unitOptions,
	unitHasError = false,
	onChangeRow,
	onChangeCell,
	onPasteMatrix,
}) => {
	const handlePaste = (
		event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		if (!onPasteMatrix) return;
		const text = event.clipboardData.getData("text");
		// Only intercept multi-cell pastes — let single-cell pastes flow through
		// to the native input so normal in-cell paste still works as expected.
		if (!text.includes("\t") && !text.includes("\n")) return;
		event.preventDefault();
		onPasteMatrix(row.id, column.localId, text);
	};

	// All description columns are procurer-editable free text, persisted in
	// `row.cells` keyed by the column's stable `localId`. Reorders/type-changes
	// can therefore never shift a value onto the wrong column.
	if (column.columnType === "description") {
		const value = row.cells?.[column.localId] ?? "";
		return (
			<td role="gridcell" className={CELL_BASE}>
				<AutoGrowCellInput
					value={value}
					onChange={(e) =>
						onChangeCell?.(row.id, column.localId, e.target.value)
					}
					onPaste={handlePaste}
					placeholder="Item description"
					aria-label={`${column.label} for row ${rowIndex + 1}`}
					data-testid={`template-row-cell-${row.id}-${column.localId}`}
				/>
			</td>
		);
	}

	if (column.localId === unitColumnId) {
		return (
			<td role="gridcell" className={CELL_BASE} style={{ minWidth: 160 }}>
				<CompactSelect
					value={row.unitId ?? ""}
					hasError={unitHasError}
					placeholder="Select unit"
					options={unitOptions}
					onChange={(e) =>
						onChangeRow(
							row.id,
							"unitId",
							e.target.value ? Number(e.target.value) : null
						)
					}
					aria-label={`Unit of measurement for row ${rowIndex + 1}`}
					data-testid={`template-row-unit-${row.id}`}
				/>
			</td>
		);
	}

	return (
		<td
			role="gridcell"
			className={cn(CELL_BASE, "bg-gray-50 italic text-tertiary-500")}
			title={cellHint(column.columnType)}
			data-testid={`template-row-${row.id}-placeholder-${column.localId}`}
		>
			{SAMPLE_CELL_LABELS[column.columnType]}
		</td>
	);
};

export default TemplateRowCell;
